import { useEffect, useState, useSyncExternalStore } from 'react';

import {
  CONNECTED_STATUS_STABILIZE_MS,
  CONNECTION_STATUS_COPY,
  RECONNECTING_STATUS_DEBOUNCE_MS,
  type ConnectionStatus,
  type ConnectionStatusViewModel,
} from './types';
import {
  getIntelligenceLiveConnectionSnapshot,
  subscribeToIntelligenceLiveConnection,
} from './intelligenceLiveConnectionStore';
import type { IntelligenceLiveConnectionSnapshot } from './types';

const SERVER_INTELLIGENCE_LIVE_CONNECTION_SNAPSHOT = {
  phase: 'idle',
  monitorCount: 0,
  updatedAt: 0,
} as const;

function getBrowserOnlineStatus() {
  if (typeof navigator === 'undefined') {
    return true;
  }

  return navigator.onLine;
}

function getServerSnapshot() {
  return SERVER_INTELLIGENCE_LIVE_CONNECTION_SNAPSHOT;
}

function getTargetStatus(
  intelligenceLiveConnection: IntelligenceLiveConnectionSnapshot,
  isBrowserOnline: boolean,
) {
  if (!isBrowserOnline) {
    return {
      status: 'disconnected' as const,
      transitionDelayMs: 0,
    };
  }

  if (intelligenceLiveConnection.phase === 'reconnecting') {
    return {
      status: 'reconnecting' as const,
      transitionDelayMs: RECONNECTING_STATUS_DEBOUNCE_MS,
    };
  }

  return {
    status: 'connected' as const,
    transitionDelayMs: CONNECTED_STATUS_STABILIZE_MS,
  };
}

export function useConnectionStatus(): ConnectionStatusViewModel {
  const intelligenceLiveConnection = useSyncExternalStore(
    subscribeToIntelligenceLiveConnection,
    getIntelligenceLiveConnectionSnapshot,
    getServerSnapshot,
  );
  const [isBrowserOnline, setIsBrowserOnline] = useState(getBrowserOnlineStatus);
  const { status: targetStatus, transitionDelayMs } = getTargetStatus(
    intelligenceLiveConnection,
    isBrowserOnline,
  );
  const [displayStatus, setDisplayStatus] = useState<ConnectionStatus>(targetStatus);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleOnline = () => {
      setIsBrowserOnline(true);
    };
    const handleOffline = () => {
      setIsBrowserOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  useEffect(() => {
    if (displayStatus === targetStatus) {
      return;
    }

    if (transitionDelayMs === 0) {
      setDisplayStatus(targetStatus);
      return;
    }

    const timer = window.setTimeout(() => {
      setDisplayStatus(targetStatus);
    }, transitionDelayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [displayStatus, targetStatus, transitionDelayMs]);

  if (displayStatus === 'connected') {
    return {
      status: displayStatus,
      hasIssue: false,
      tone: null,
      title: null,
      detail: null,
      srLabel: 'Connection healthy.',
    };
  }

  const copy = CONNECTION_STATUS_COPY[displayStatus];

  return {
    status: displayStatus,
    hasIssue: true,
    tone: copy.tone,
    title: copy.title,
    detail: copy.detail,
    srLabel: copy.srLabel,
  };
}
